import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { useCustomMutation } from "@refinedev/core";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { useQueryClient } from "@tanstack/react-query";

// The layout can unmount/remount this component while app data loads, which
// would re-run the effect and send the same one-time authCode twice. Component
// state/refs don't survive a remount, so track in-flight requests at module
// level and let a remounted instance attach to the existing one.
const submittedRequests = new Map<string, Promise<void>>();

export const ReAuthenticationCallback = () => {
  const { translate } = useTranslation("pages.stores.reAuthentication");
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { mutateAsync } = useCustomMutation();
  const queryClient = useQueryClient();
  const [authorizationState, setAuthorizationState] = useState<
    "initial" | "loading" | "error"
  >("initial");

  useEffect(() => {
    tryReAuthorize();
  }, []);

  const handleReAuthorization = (
    authCode: string,
    state: string,
    amazonSellerId: string,
    marketplaceId: string,
  ) => {
    setAuthorizationState("loading");

    let request = submittedRequests.get(authCode);

    if (!request) {
      // mutate()-level callbacks are dropped if this component unmounts
      // mid-request, so the success handling lives in the shared promise.
      request = mutateAsync({
        // PUT = re-authorize an existing store (POST creates a new one and
        // fails for a store that already exists).
        url: "authorization/complete",
        method: "put",
        values: {
          authCode,
          state,
          amazonSellerId,
          marketplaceId: parseInt(marketplaceId),
        },
      }).then(
        () => {
          queryClient.invalidateQueries({
            queryKey: ["initialMarketplace"],
          });

          // Clear re-authentication flags
          localStorage.removeItem("reAuthenticating");
          localStorage.removeItem("marketplaceId");

          setTimeout(() => {
            window.location.assign("/"); // Full reload
          }, 1000);
        },
        (error) => {
          submittedRequests.delete(authCode);
          throw error;
        },
      );
      submittedRequests.set(authCode, request);
    }

    // Every mounted instance reflects the failure; unmounted ones no-op.
    request.catch(() => setAuthorizationState("error"));
  };

  const tryReAuthorize = () => {
    const authCode = searchParams.get("spapi_oauth_code");
    const amazonSellerId = searchParams.get("selling_partner_id");
    const state = searchParams.get("state");
    const isReAuthenticating = localStorage.getItem("reAuthenticating");
    const marketplaceId = localStorage.getItem("marketplaceId");

    if (
      authCode &&
      state &&
      isReAuthenticating &&
      marketplaceId &&
      amazonSellerId
    ) {
      handleReAuthorization(authCode, state, amazonSellerId, marketplaceId);
    } else {
      // If not in re-authentication flow, redirect to home
      navigate("/");
    }
  };

  return (
    <Box width="100%" display="flex" justifyContent="center">
      <Card
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          padding: "24px",
          width: "fit-content",
          height: "200px",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6">
          {authorizationState === "error"
            ? translate("error")
            : translate("reAuthorizing")}
        </Typography>
        {authorizationState === "error" ? (
          <Button variant="contained" onClick={tryReAuthorize}>
            {translate("tryAgain")}
          </Button>
        ) : (
          <CircularProgress />
        )}
      </Card>
    </Box>
  );
};
